import { computed, inject, nextTick, provide, ref, watch, type ComputedRef, type InjectionKey, type Ref } from 'vue';
import { routeLocationKey } from 'vue-router';

export type ModalDirtyCheck = () => boolean;

export interface RetainedModalDirtyRegistry {
    /** Registers a dirty check for the content currently mounted in the modal; returns the unregister. */
    register: (check: ModalDirtyCheck) => () => void;
}

/** Provided by every base modal so the form it hosts can declare its own unsaved-changes check. */
export const RETAINED_MODAL_DIRTY_KEY: InjectionKey<RetainedModalDirtyRegistry> = Symbol('retainedModalDirty');

export type RetainedModalEvent = 'modal:dismiss' | 'modal:discard';

export interface RetainedModalContentOptions {
    /** The base modal's current model value. */
    open: () => boolean;
    /** Writes the model with no cancel semantics — a dismissal. */
    setOpen: (value: boolean) => void;
    /** The base's own cancel path (its cancel emit, the model write, any chrome reset). Used when the X discards. */
    cancel: () => void;
    retainOnDismiss: () => boolean;
    /** Identity of the record the contents belong to. A change discards them. */
    retainKey?: () => unknown;
    /** A host-owned dirty check (for forms that live outside the modal's slot). */
    isDirty?: () => ModalDirtyCheck | null | undefined;
    emit: (event: RetainedModalEvent) => void;
}

export interface RetainedModalContent {
    /** Bind to the dialog library's unmount strategy. `true` whenever nothing is retained — today's value. */
    unmountWhenHidden: ComputedRef<boolean>;
    /** Key the content wrapper on this so a record change while open mounts a fresh form. */
    contentKey: Ref<number>;
    confirmOpen: Ref<boolean>;
    /** Outside-click / Escape: hide, keep the contents. */
    dismiss: () => void;
    /** The X: confirm when dirty, otherwise discard and close. */
    requestClose: () => void;
    keepEditing: () => void;
    confirmDiscard: () => void;
    /** Throw the retained contents away (unmounts them). */
    discard: () => void;
    /** For bases that receive Escape themselves (reka's listener is registered before HeadlessUI's). */
    onEscape: () => void;
    /** HeadlessUI bases only: call from the transition's after-leave so focus returns to the opener. */
    afterLeave: () => void;
    isDirty: () => boolean;
}

/**
 * The retention state machine shared by the base modals (CU-86d3wvwq7).
 *
 * Contents survive a dismissal because the modal is hidden rather than unmounted: the base binds
 * `unmountWhenHidden` to HeadlessUI's `unmount` / reka's `unmountOnHide`. The expression is `true`
 * until the first open and again after every discard, so the render path in those states is the
 * one every non-retaining modal takes today.
 *
 * Any close the base did not initiate through `dismiss()` — a submit, an in-form Cancel, a parent
 * writing the model — discards, so the next open is clean without the host learning a new API.
 */
export function useRetainedModalContent(options: RetainedModalContentOptions): RetainedModalContent {
    const mounted = ref(false);
    const pulse = ref(false);
    const contentKey = ref(0);
    const confirmOpen = ref(false);
    const checks = new Set<ModalDirtyCheck>();
    let pendingReason: 'dismiss' | 'discard' | null = null;
    let opener: HTMLElement | null = null;

    const retaining = () => options.retainOnDismiss() && mounted.value;

    const unmountWhenHidden = computed(() => pulse.value || !(options.retainOnDismiss() && mounted.value));

    provide(RETAINED_MODAL_DIRTY_KEY, {
        register(check) {
            checks.add(check);

            return () => {
                checks.delete(check);
            };
        },
    });

    function isDirty(): boolean {
        const hostCheck = options.isDirty?.();
        if (typeof hostCheck === 'function' && hostCheck()) {
            return true;
        }

        for (const check of checks) {
            if (check()) {
                return true;
            }
        }

        return false;
    }

    function discard(): void {
        confirmOpen.value = false;

        if (!mounted.value) {
            return;
        }

        mounted.value = false;
        options.emit('modal:discard');
    }

    /** Clears a pending reason the model write did not consume (the parent refused the close). */
    function expirePendingReason(): void {
        nextTick(() => {
            if (options.open()) {
                pendingReason = null;
            }
        });
    }

    function dismiss(): void {
        if (!retaining()) {
            options.setOpen(false);

            return;
        }

        pendingReason = 'dismiss';
        options.setOpen(false);
        options.emit('modal:dismiss');
        expirePendingReason();
    }

    function discardAndClose(): void {
        pendingReason = 'discard';
        options.cancel();
        expirePendingReason();
    }

    function requestClose(): void {
        if (!retaining()) {
            options.cancel();

            return;
        }

        if (isDirty()) {
            confirmOpen.value = true;

            return;
        }

        discardAndClose();
    }

    function keepEditing(): void {
        confirmOpen.value = false;
    }

    function confirmDiscard(): void {
        confirmOpen.value = false;
        discardAndClose();
    }

    function onEscape(): void {
        if (confirmOpen.value) {
            keepEditing();

            return;
        }

        if (retaining()) {
            dismiss();

            return;
        }

        options.setOpen(false);
    }

    /**
     * HeadlessUI restores focus itself when the panel is removed, but not when it is merely
     * hidden (its check is `activeElement === body` after removal), so a dismissal does it here.
     * reka needs none of this — its `FocusScope` restores focus whenever `present` flips false.
     */
    function afterLeave(): void {
        const target = opener;
        opener = null;

        if (!options.retainOnDismiss() || !target?.isConnected) {
            return;
        }

        if (document.activeElement === document.body || document.activeElement === null) {
            target.focus();
        }
    }

    watch(
        options.open,
        async (open) => {
            if (open) {
                if (!options.retainOnDismiss()) {
                    return;
                }

                opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
                const reshow = mounted.value;
                mounted.value = true;

                if (!reshow) {
                    return;
                }

                // HeadlessUI's TransitionChild, re-shown with `unmount=false`, marks itself visible
                // without re-registering with its parent's nesting registry (the effect that would
                // register it never re-runs), so the next leave hides the root before the children
                // have faded. Flipping the strategy for one tick after a re-show makes that effect
                // re-run and register; while visible the flip renders nothing differently.
                await nextTick();
                pulse.value = true;
                await nextTick();
                pulse.value = false;

                return;
            }

            const reason = pendingReason;
            pendingReason = null;

            if (reason !== 'dismiss') {
                discard();
            }
        },
        // A host that renders its modal with the model already `true` never fires this watcher
        // otherwise, so `mounted` would stay false and the instance would silently not retain.
        // With `open` false the immediate run reaches `discard()`, which no-ops on `!mounted`.
        { immediate: true },
    );

    if (options.retainKey) {
        watch(options.retainKey, (next, previous) => {
            if (next === previous || !mounted.value) {
                return;
            }

            if (options.open()) {
                contentKey.value += 1;
                options.emit('modal:discard');

                return;
            }

            discard();
        });
    }

    const route = inject(routeLocationKey, null);

    if (route) {
        watch(
            () => route.path,
            (next, previous) => {
                if (next === previous || !mounted.value) {
                    return;
                }

                // `App.vue` renders the RouterView without a `:key`, so navigating between two
                // orders reuses the host and every modal it owns. Contents captured on the
                // previous record must not survive that, whether they are hidden or on screen —
                // an open modal is closed as well, because it is still bound to the record the
                // operator has just navigated away from.
                if (options.open()) {
                    discardAndClose();

                    return;
                }

                discard();
            },
        );
    }

    return {
        unmountWhenHidden,
        contentKey,
        confirmOpen,
        dismiss,
        requestClose,
        keepEditing,
        confirmDiscard,
        discard,
        onEscape,
        afterLeave,
        isDirty,
    };
}
